/**
 * Synthetic rhythm signals (W2-08 timing): the metronome click track of the chart fixtures
 * (fixtures/README.md "Synthetic fixtures": a click on every beat, accent every 4 beats, a soft 220 Hz pad and
 * 0.5 s fades, generated by fixtures/tools/make_synthetic.py), re-synthesised in TypeScript so the tempo and offset
 * detectors can be tested and demonstrated on exact ground truth without an Ogg decoder. Pure (no Web Audio).
 *
 * Clicks: a sine (1760 Hz accent / 880 Hz) of 80 ms with an exp(-40 t) decay, amplitudes 0.6 / 0.35, starting at the
 * sample `floor(time * sampleRate)`. Optional extras make the signal harder: a kick drum on the beats instead of the
 * clicks (a pitch sweep from 150 Hz down to 50 Hz, optionally with a short noise transient), off-beat hi-hats
 * (short high-passed noise bursts), deterministic white noise, and per-click timing jitter.
 */

export interface ClickTrackOptions {
  readonly sampleRate?: number;
  /** Track length in seconds. */
  readonly lengthSec: number;
  /** Tempo points: from `beat` on, `bpm` (first point at beat 0). Default one point from `bpm`. */
  readonly tempo?: ReadonlyArray<{ readonly beat: number; readonly bpm: number }>;
  readonly bpm?: number;
  /** Music time (s) of beat 0. */
  readonly offsetSec?: number;
  /** Beats per bar for the accents (default 4). */
  readonly beatsPerBar?: number;
  /** Soft pad and fades like the fixtures (default true). */
  readonly pad?: boolean;
  /** Add a short noise burst between beats (an eighth-note hi-hat), of this amplitude. */
  readonly offbeatHat?: number;
  /** A kick drum of this amplitude on every beat instead of the click. */
  readonly kick?: number;
  /** Add a 4 ms noise transient (the beater) to the kick, at half its amplitude. */
  readonly kickTransient?: boolean;
  /** White noise amplitude (deterministic). */
  readonly noise?: number;
  /** Random timing error of each click, uniform in +-jitterSec (deterministic). */
  readonly jitterSec?: number;
  /** Seed of the deterministic noise and jitter. */
  readonly seed?: number;
}

export const CLICK_SAMPLE_RATE = 44100;

/** Mulberry32: a small deterministic generator in [0, 1). */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Seconds of beat `b` (f64, Phira order: accumulated segment times). */
export function beatTime(tempo: ReadonlyArray<{ readonly beat: number; readonly bpm: number }>, b: number): number {
  let t = 0;
  let prevBeat = 0;
  let prevBpm = tempo[0]?.bpm ?? 120;
  for (let i = 1; i < tempo.length; i++) {
    const p = tempo[i];
    if (p === undefined || p.beat > b) break;
    t += (p.beat - prevBeat) * (60 / prevBpm);
    prevBeat = p.beat;
    prevBpm = p.bpm;
  }
  return t + (b - prevBeat) * (60 / prevBpm);
}

function addClick(out: Float32Array, start: number, freq: number, amp: number, sampleRate: number): void {
  const len = Math.floor(sampleRate * 0.08);
  for (let i = 0; i < len; i++) {
    const s = start + i;
    if (s < 0) continue;
    if (s >= out.length) break;
    const t = i / sampleRate;
    out[s] = (out[s] ?? 0) + amp * Math.sin(2 * Math.PI * freq * t) * Math.exp(-t * 40);
  }
}

/** A hi-hat: white noise through a second difference (+12 dB per octave: the energy sits in the highs). */
function addHat(out: Float32Array, start: number, amp: number, sampleRate: number, next: () => number): void {
  const len = Math.floor(sampleRate * 0.03);
  let n1 = 0;
  let n2 = 0;
  for (let i = 0; i < len; i++) {
    const n0 = next() * 2 - 1;
    const hp = (n0 - 2 * n1 + n2) / 2;
    n2 = n1;
    n1 = n0;
    const s = start + i;
    if (s < 0) continue;
    if (s >= out.length) break;
    const t = i / sampleRate;
    out[s] = (out[s] ?? 0) + amp * hp * Math.exp(-t * 150);
  }
}

/** A kick drum: a sine sweeping from 150 Hz down to 50 Hz over 300 ms with an exp(-10 t) decay. */
function addKick(
  out: Float32Array,
  start: number,
  amp: number,
  transient: boolean,
  sampleRate: number,
  next: () => number,
): void {
  const len = Math.floor(sampleRate * 0.3);
  const click = Math.floor(sampleRate * 0.004);
  let phase = 0;
  for (let i = 0; i < len; i++) {
    const t = i / sampleRate;
    phase += (2 * Math.PI * (50 + 100 * Math.exp(-t * 30))) / sampleRate;
    let v = amp * Math.sin(phase) * Math.exp(-t * 10);
    if (transient && i < click) v += 0.5 * amp * (next() * 2 - 1) * Math.exp(-t * 1000);
    const s = start + i;
    if (s < 0) continue;
    if (s >= out.length) break;
    out[s] = (out[s] ?? 0) + v;
  }
}

/** The click track (mono). */
export function clickTrack(opts: ClickTrackOptions): Float32Array {
  const sr = opts.sampleRate ?? CLICK_SAMPLE_RATE;
  const length = Math.max(0, Math.floor(sr * opts.lengthSec));
  const out = new Float32Array(length);
  const tempo = opts.tempo ?? [{ beat: 0, bpm: opts.bpm ?? 120 }];
  const offset = opts.offsetSec ?? 0;
  const perBar = Math.max(1, Math.round(opts.beatsPerBar ?? 4));
  const next = rng(opts.seed ?? 1);
  const jitter = opts.jitterSec ?? 0;
  for (let b = 0; ; b++) {
    const t = beatTime(tempo, b) + offset;
    if (t >= opts.lengthSec) break;
    const dt = jitter > 0 ? (next() * 2 - 1) * jitter : 0;
    const accent = b % perBar === 0;
    const kick = opts.kick ?? 0;
    if (kick > 0) addKick(out, Math.floor((t + dt) * sr), kick, opts.kickTransient === true, sr, next);
    else addClick(out, Math.floor((t + dt) * sr), accent ? 1760 : 880, accent ? 0.6 : 0.35, sr);
    const hat = opts.offbeatHat ?? 0;
    if (hat > 0) {
      const half = (beatTime(tempo, b) + beatTime(tempo, b + 1)) / 2 + offset;
      addHat(out, Math.floor(half * sr), hat, sr, next);
    }
  }
  const noise = opts.noise ?? 0;
  const pad = opts.pad ?? true;
  for (let i = 0; i < length; i++) {
    const t = i / sr;
    let v = out[i] ?? 0;
    if (pad) v += 0.04 * Math.sin(2 * Math.PI * 220 * t);
    if (noise > 0) v += noise * (next() * 2 - 1);
    if (pad) v *= Math.max(0, Math.min(1, t / 0.5, (opts.lengthSec - t) / 0.5));
    out[i] = v;
  }
  return out;
}
